import React, { useCallback, useMemo, useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import moment from "moment";
import dayjs from "dayjs";
import { BLUE_RGB } from "utils/colorsTheme";
import { createSelector } from "reselect";
import { useParams, useHistory } from "react-router-dom";

// @material-ui/icons

// components
import GridContainer from "components/Grid/GridContainer.js";
import Snackbars from "components/Snackbar/Snackbar.js";
import GridItem from "components/Grid/GridItem.js";
import CardGraph from "components/CardGraph/CardGraph";
import SelectDateMenu from "components/SelectDateMenu/SelectDateMenu";

// action creators
import {
  getEvents,
  changeStartDate,
  changeEndDate,
  setError,
  clearError,
} from "redux/ducks/giotanksGraph";

import "./styles.css";

const optionsTime = {
  maintainAspectRatio: true,
  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          max: 100,
        },
      },
    ],
  },
  lineAtIndex: 2,
};

// Custom selectors
const startDateSelector = createSelector(
  (state) => state.giotanksGraph.startDate,
  (startDate) =>
    startDate
      ? moment(startDate, "YYYY-MM-DD").subtract(3, "months")
      : moment().subtract(3, "months")
);
const endDateSelector = createSelector(
  (state) => state.giotanksGraph.endDate,
  (endDate) => (endDate ? moment(endDate, "YYYY-MM-DD") : moment())
);

export const GiotanksGraph = () => {
  const { id, serie } = useParams();
  const dispatch = useDispatch();
  let history = useHistory();
  // selectors
  const eventsAxeX = useSelector((state) => state.giotanksGraph.eventsAxeX);
  const eventsAxeY = useSelector((state) => state.giotanksGraph.eventsAxeY);
  const startDate = useSelector((state) => startDateSelector(state));
  const endDate = useSelector((state) => endDateSelector(state));
  const { error, errorMessage } = useSelector(
    (state) => state.giotanksGraph,
    shallowEqual
  );

  const getDataGraph = () => {
    return {
      labels: eventsAxeX,
      datasets: [
        {
          label: "% de Gas",
          data: eventsAxeY,
          borderColor: BLUE_RGB,
          backgroundColor: "rgba(0,79, 159,.3)",
        },
      ],
    };
  };
  const data = useMemo(
    () => getDataGraph(),
    [eventsAxeX, eventsAxeY]
  );
  // handlers
  const handleSubmit = () => {
    if (!startDate) {
      setSnackbar("No se ha seleccionado fecha de inicio.");
      return;
    }
    if (!endDate) {
      setSnackbar("No se ha seleccionado fecha final.");
      return;
    }

    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    fetchEvents(id, start, end);
  };
  const handleGoBack = (page) => {
    history.goBack();
  };

  // calls
  const fetchEvents = useCallback(
    (id, startDate, endDate) => {
      dispatch(getEvents(id, startDate, endDate));
    },
    [dispatch]
  );
  const changeStartDateMemoized = useCallback(
    (date) => {
      dispatch(changeStartDate(dayjs(date).format("YYYY-MM-DD")));
    },
    [dispatch]
  );
  const changeEndDateMemoized = useCallback(
    (date) => {
      dispatch(changeEndDate(dayjs(date).format("YYYY-MM-DD")));
    },
    [dispatch]
  );
  const setSnackbar = useCallback((error) => {
    dispatch(setError(error));
  }, []);
  const clearSnackbar = useCallback(() => {
    dispatch(clearError());
  }, []);

  // Effects
  useEffect(() => {
    if (id) {
      const start = dayjs(startDate).format("YYYY-MM-DD");
      const end = dayjs(endDate).format("YYYY-MM-DD");
      fetchEvents(id, start, end);
    }
  }, []);

  return (
    <>
      <SelectDateMenu
        onChangeStartDate={changeStartDateMemoized}
        onChangeEndDate={changeEndDateMemoized}
        dates={{ startDate, endDate }}
        onSubmit={handleSubmit}
        color="info"
      />
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <CardGraph
            isVertical
            hasGoBack
            title={`Mediciones por fecha - dispositivo #${serie}`}
            height={120}
            data={data}
            onChangePage={handleGoBack}
            options={optionsTime}
          />
        </GridItem>
      </GridContainer>
      <Snackbars
        place="tr"
        color="danger"
        message={errorMessage}
        open={error}
        closeNotification={() => clearSnackbar()}
        close
      />
    </>
  );
};
